import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, Clock, Calendar, Users, LogIn } from 'lucide-react';
import { getDoc, doc, addDoc, collection, serverTimestamp, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import logger from '../../utils/logger';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import PositionBadge from '../../components/ui/PositionBadge';
import TeamFlag from '../../components/ui/TeamFlag';
import { parseLocalDate } from '../../utils/calculations';

const DAY_LABELS = { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sáb', dom: 'Dom' };
const ALL_DAYS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];
const INVITE_SCOPE = {
  GROUP: 'group',
  GAME: 'game',
};

const PlayerPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { groups, allGames, userId: myUserId, profile: myProfile } = useAppContext();
  const { showError, showSuccess } = useToast();
  const [playerProfile, setPlayerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteStatuses, setInviteStatuses] = useState({});
  const [invitingKey, setInvitingKey] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // Grupos onde sou admin
  const myAdminGroups = useMemo(() =>
    groups.filter(g => g.ownerId === myUserId || g.adminIds?.includes(myUserId)),
    [groups, myUserId]
  );
  // Só mostrar se estou a ver o perfil de outro utilizador
  const isOtherUser = userId !== myUserId;

  const activeGamesByGroup = useMemo(() => {
    const result = {};
    myAdminGroups.forEach(group => {
      const nextGame = allGames
        .filter(g => g.groupId === group.id && g.status === 'scheduled')
        .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date))[0];
      if (nextGame) result[group.id] = nextGame;
    });
    return result;
  }, [allGames, myAdminGroups]);

  const getInviteKey = (scope, groupId, gameId) => `${scope}:${groupId}:${gameId || '-'}`;

  const getInviteDocRef = (scope, groupId, targetUserId, gameId) => {
    if (scope === INVITE_SCOPE.GAME) {
      return doc(db, 'groups', groupId, 'games', gameId, 'playerInvites', targetUserId);
    }
    return doc(db, 'groups', groupId, 'playerInvites', targetUserId);
  };

  const handleInvite = async ({ scope, group, game }) => {
    const inviteKey = getInviteKey(scope, group.id, game?.id);
    const currentStatus = inviteStatuses[inviteKey];
    if (invitingKey || currentStatus === 'pending') return;

    setInvitingKey(inviteKey);
    setInviteStatuses(prev => ({
      ...prev,
      [inviteKey]: 'pending',
    }));
    try {
      const invitePayload = {
        scope,
        groupId: group.id,
        groupName: group.name,
        sport: group.sport || null,
        courtName: group.courtName || null,
        courtAddress: group.courtAddress || null,
        city: group.city || null,
        dayOfWeek: group.dayOfWeek || null,
        defaultTime: group.defaultTime || null,
        gameId: game?.id || null,
        gameDate: game?.date || null,
        gameTime: game?.time || null,
        fromUserId: myUserId,
        fromUserName: myProfile?.name || '',
      };

      await setDoc(getInviteDocRef(scope, group.id, userId, game?.id), {
        ...invitePayload,
        status: 'pending',
        targetUserId: userId,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await addDoc(collection(db, 'users', userId, 'invites'), {
        ...invitePayload,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      showSuccess(scope === INVITE_SCOPE.GAME ? 'Convite para o jogo enviado.' : 'Convite para o grupo enviado.');
    } catch (err) {
      logger.error('Erro ao enviar convite:', err);
      setInviteStatuses(prev => ({
        ...prev,
        [inviteKey]: currentStatus || null,
      }));
      showError('Erro ao enviar convite.');
    } finally {
      setInvitingKey(null);
    }
  };

  useEffect(() => {
    if (!isOtherUser || myAdminGroups.length === 0) return undefined;

    const unsubs = [];

    myAdminGroups.forEach(group => {
      const groupKey = getInviteKey(INVITE_SCOPE.GROUP, group.id);
      unsubs.push(onSnapshot(getInviteDocRef(INVITE_SCOPE.GROUP, group.id, userId), (snap) => {
        setInviteStatuses(prev => ({
          ...prev,
          [groupKey]: snap.exists() ? snap.data().status || 'pending' : null,
        }));
      }));

      const activeGame = activeGamesByGroup[group.id];
      if (activeGame) {
        const gameKey = getInviteKey(INVITE_SCOPE.GAME, group.id, activeGame.id);
        unsubs.push(onSnapshot(getInviteDocRef(INVITE_SCOPE.GAME, group.id, userId, activeGame.id), (snap) => {
          setInviteStatuses(prev => ({
            ...prev,
            [gameKey]: snap.exists() ? snap.data().status || 'pending' : null,
          }));
        }));
      }
    });

    return () => unsubs.forEach(unsub => unsub());
  }, [activeGamesByGroup, isOtherUser, myAdminGroups, userId]);

  const getInviteButton = (scope, group, game) => {
    const inviteKey = getInviteKey(scope, group.id, game?.id);
    const status = inviteStatuses[inviteKey];
    const isLoading = invitingKey === inviteKey;
    const isGameInvite = scope === INVITE_SCOPE.GAME;

    if (status === 'pending') {
      return {
        disabled: true,
        className: isGameInvite ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-slate-100 text-slate-600 border border-slate-200',
        label: 'Pendente',
      };
    }
    if (status === 'declined') {
      return {
        disabled: isLoading,
        className: isGameInvite ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-slate-100 text-slate-700 border border-slate-200',
        label: isLoading ? 'Enviando...' : 'Reenviar',
      };
    }
    return {
      disabled: isLoading,
      className: isGameInvite
        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-50'
        : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 disabled:opacity-50',
      label: isLoading ? 'Enviando...' : (scope === INVITE_SCOPE.GAME ? 'Convidar para o próximo jogo' : 'Convidar ao grupo'),
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', userId, 'profile', 'data'));
        if (snap.exists()) {
          setPlayerProfile({ id: userId, ...snap.data() });
        } else {
          setPlayerProfile(null);
        }
      } catch (err) {
        logger.error('Erro ao carregar perfil do jogador:', err);
        setPlayerProfile(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-400">Carregando...</div>
      </div>
    );
  }

  if (!playerProfile) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="text-4xl mb-4">👤</div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Jogador não encontrado</h2>
        <p className="text-slate-500 text-sm mb-6">Este perfil não existe ou foi removido.</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
      </div>
    );
  }

  const ratingHistory = (playerProfile.ratingHistory || []).slice().reverse().slice(0, 5);
  const availability = playerProfile.availability || {};
  // Suportar formato novo (objecto por dia) e formato antigo (days[])
  const isNewFormat = typeof availability.seg !== 'undefined' || typeof availability.ter !== 'undefined';
  const availDays = isNewFormat
    ? Object.entries(availability).filter(([, v]) => v !== null).map(([k]) => k)
    : (availability.days || []);

  return (
    <div className="bg-slate-50 min-h-screen pb-8">
      {/* Back button */}
      <div className="p-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
      </div>

      {/* Profile card */}
      <div className="px-4 space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center gap-3">
          <button
            onClick={() => setShowPhotoModal(true)}
            className="rounded-full ring-2 ring-transparent hover:ring-blue-300 transition-all"
            title="Ver foto"
          >
            <PlayerAvatar user={playerProfile} size="lg" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{playerProfile.name}</h1>
            {playerProfile.city && (
              <p className="text-slate-500 text-sm flex items-center justify-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" /> {playerProfile.city}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {playerProfile.position && <PositionBadge position={playerProfile.position} />}
            {playerProfile.heartTeam && playerProfile.heartTeam !== 'Sem Time' && (
              <TeamFlag team={playerProfile.heartTeam} />
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 flex-wrap justify-center">
            {playerProfile.communityRating != null && (
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="text-xl font-bold text-amber-500">{playerProfile.communityRating.toFixed(1)}</span>
                </div>
                <span className="text-xs text-slate-400">nota galera</span>
              </div>
            )}
            {playerProfile.manualRating != null && (
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-slate-400 text-slate-400" />
                  <span className="text-xl font-bold text-slate-500">{typeof playerProfile.manualRating === 'number' ? playerProfile.manualRating.toFixed(1) : playerProfile.manualRating}</span>
                </div>
                <span className="text-xs text-slate-400">auto-avaliação</span>
              </div>
            )}
          </div>
          {playerProfile.whatsappPhone && (
            <a
              href={`https://wa.me/55${playerProfile.whatsappPhone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Chamar no WhatsApp
            </a>
          )}
        </div>

        {/* Availability */}
        {(availDays.length > 0 || availability.timeStart || availability.timeEnd) && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-400" /> Disponibilidade
            </h2>
            {isNewFormat ? (
              <div className="space-y-2">
                {ALL_DAYS.map(day => {
                  const dayData = availability[day];
                  if (!dayData) return null;
                  // Suportar formato antigo { start, end } e novo (array)
                  const intervals = Array.isArray(dayData) ? dayData : [dayData];
                  return (
                    <div key={day} className="flex items-start gap-2">
                      <span className="w-10 text-xs font-bold bg-emerald-500 text-white px-2 py-1 rounded-lg text-center shrink-0 mt-0.5">{DAY_LABELS[day]}</span>
                      <div className="flex flex-col gap-1">
                        {intervals.map((iv, idx) => (
                          <div key={idx} className="flex items-center gap-1 text-xs text-slate-600">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span>{iv.start || '--:--'} – {iv.end || '--:--'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                {availDays.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {ALL_DAYS.map(day => (
                      <span
                        key={day}
                        className={`px-2 py-1 rounded-lg text-xs font-bold ${availDays.includes(day) ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`}
                      >
                        {DAY_LABELS[day]}
                      </span>
                    ))}
                  </div>
                )}
                {(availability.timeStart || availability.timeEnd) && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>{availability.timeStart || '--:--'} – {availability.timeEnd || '--:--'}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Meus grupos */}
        {isOtherUser && myAdminGroups.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2 px-1">
              <Users className="w-4 h-4 text-blue-500" /> Meus grupos
            </h2>
            {myAdminGroups.map(group => {
                const activeGame = activeGamesByGroup[group.id];
                const isMember = (group.memberIds || []).includes(userId);
                const isConfirmedForGame = activeGame
                  ? (activeGame.confirmedIds || []).includes(userId) || (activeGame.confirmedExternalCheckins || []).includes(userId)
                  : false;
                const isPendingExternalCheckin = activeGame ? (activeGame.externalCheckins || []).includes(userId) : false;
                const groupStatus = inviteStatuses[getInviteKey(INVITE_SCOPE.GROUP, group.id)];
                const effectiveGroupStatus = !isMember && groupStatus === 'accepted' ? null : groupStatus;
                const gameStatus = activeGame ? inviteStatuses[getInviteKey(INVITE_SCOPE.GAME, group.id, activeGame.id)] : null;
                const effectiveGameStatus = !isMember && !isPendingExternalCheckin && !isConfirmedForGame && gameStatus === 'accepted' ? null : gameStatus;
                const showGameInvite = !isMember && !!activeGame && !isPendingExternalCheckin && !isConfirmedForGame;
                const gameInviteButton = activeGame ? {
                  ...getInviteButton(INVITE_SCOPE.GAME, group, activeGame),
                  label: effectiveGameStatus === 'declined' ? 'Reenviar para o próximo jogo' : getInviteButton(INVITE_SCOPE.GAME, group, activeGame).label,
                } : null;
                const groupInviteButton = {
                  ...getInviteButton(INVITE_SCOPE.GROUP, group),
                  label: effectiveGroupStatus === 'declined' ? 'Reenviar' : getInviteButton(INVITE_SCOPE.GROUP, group).label,
                };

                return (
                  <div key={group.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-slate-700">{group.name}</div>
                        {!isMember && (
                          <button
                            onClick={() => handleInvite({ scope: INVITE_SCOPE.GROUP, group })}
                            disabled={groupInviteButton.disabled}
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${groupInviteButton.className}`}
                            title="Convidar ao grupo"
                          >
                            <LogIn className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {group.city && <div className="text-xs text-slate-400">{group.city}</div>}
                      {isMember && (
                        <div className="text-[11px] text-emerald-600 mt-1 font-medium">
                          Já é membro deste grupo
                        </div>
                      )}
                      {!isMember && effectiveGroupStatus === 'pending' && (
                        <div className="text-[11px] text-slate-500 mt-1 font-medium">
                          Convite para o grupo enviado
                        </div>
                      )}
                      {activeGame && (
                        <div className="text-[11px] text-slate-400 mt-1">
                          Próximo jogo: {parseLocalDate(activeGame.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}{activeGame.time ? ` às ${activeGame.time}` : ''}
                        </div>
                      )}
                    </div>
                    {!isMember && (
                      <div className="flex flex-col gap-2 sm:shrink-0 sm:min-w-[220px]">
                        {showGameInvite && (
                          <button
                            onClick={() => handleInvite({ scope: INVITE_SCOPE.GAME, group, game: activeGame })}
                            disabled={gameInviteButton?.disabled}
                            className={`w-full text-xs font-bold px-3 py-2 rounded-lg text-center transition-colors ${gameInviteButton.className}`}
                          >
                            {gameInviteButton.label}
                          </button>
                        )}
                        {!showGameInvite && activeGame && isPendingExternalCheckin && (
                          <span className="w-full text-xs font-bold px-3 py-2 rounded-lg text-center bg-amber-100 text-amber-700 border border-amber-200">
                            Check-in já solicitado
                          </span>
                        )}
                        {!showGameInvite && activeGame && isConfirmedForGame && (
                          <span className="w-full text-xs font-bold px-3 py-2 rounded-lg text-center bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Já confirmado no jogo
                          </span>
                        )}
                        {!activeGame && (
                          <span className="w-full text-xs font-bold px-3 py-2 rounded-lg text-center bg-slate-100 text-slate-600 border border-slate-200">
                            Sem jogo agendado
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
            })}
          </div>
        )}

        {/* Rating history */}
        {ratingHistory.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" /> Últimas Notas
            </h2>
            <div className="flex flex-wrap gap-2">
              {ratingHistory.map((h, i) => (
                <span
                  key={i}
                  className={`px-3 py-1.5 rounded-xl text-sm font-bold ${h.rating >= 7 ? 'bg-amber-100 text-amber-700' : h.rating >= 5 ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-600'}`}
                >
                  ★ {h.rating}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal: Foto ampliada */}
      {showPhotoModal && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/75 backdrop-blur-sm"
          onClick={() => setShowPhotoModal(false)}
        >
          <div className="flex flex-col items-center px-6" onClick={e => e.stopPropagation()}>
            {playerProfile.photoURL ? (
              <img
                src={playerProfile.photoURL}
                alt={playerProfile.name}
                className="w-44 h-44 rounded-full ring-4 ring-white shadow-2xl object-cover"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-44 h-44 rounded-full bg-white ring-4 ring-white shadow-2xl flex items-center justify-center text-8xl">
                {playerProfile.photo || '👤'}
              </div>
            )}
            <p className="text-white font-bold text-xl mt-4 text-center drop-shadow">{playerProfile.name}</p>
            {playerProfile.heartTeam && playerProfile.heartTeam !== 'Sem Time' && (
              <div className="mt-2 flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full">
                <TeamFlag team={playerProfile.heartTeam} />
                <span className="text-white text-sm font-medium">{playerProfile.heartTeam}</span>
              </div>
            )}
            <button
              onClick={() => setShowPhotoModal(false)}
              className="mt-8 text-white/60 text-xs hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerPage;
